import { useMemo, useState } from 'react'
import { ListChecks, Plus } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useInitFromServer } from '@/hooks/useInitFromServer'
import {
  DutyCard,
  DutyCardBody,
  DutyCardContent,
  DutyCardEditFooter,
  DutyCardHeader,
} from '@/features/duties/components/cards/shared/DutyCard'
import type { EvalCaseAssertion } from '../../types'
import { useEvalCaseAssertionsQuery } from '../../hooks/useEvalCaseAssertionsQuery'
import {
  useSaveAssertionsMutation,
  type DraftAssertion,
} from '../../hooks/useSaveAssertionsMutation'
import { AssertionEditRow } from './AssertionEditRow'
import { formatSeverity, severityClassName } from './assertionPresentation'

function toDraft(assertion: EvalCaseAssertion): DraftAssertion {
  return {
    id: assertion.id,
    name: assertion.name,
    criteria: assertion.criteria,
    severity: assertion.severity,
  }
}

export function EvalAssertionsCard({ caseId }: { caseId: string }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [draft, setDraft] = useState<DraftAssertion[]>([])
  const assertionsQuery = useEvalCaseAssertionsQuery(caseId)
  const saveMutation = useSaveAssertionsMutation(caseId)

  const server = useMemo(
    () =>
      [...(assertionsQuery.data?.items ?? [])]
        .filter((row) => row.active)
        .toSorted((left, right) => left.position - right.position),
    [assertionsQuery.data]
  )

  useInitFromServer(server, (rows) => setDraft(rows.map(toDraft)))

  // TODO(routing PR 6 follow-up): register this card with
  // `useRegisterTabEditor` (hooks/useRegisterTabEditor) so the
  // close dialog can save it. Pattern: EvalPromptCard.
  const isDirty = useMemo(() => {
    const baseline = server.map(toDraft)
    return JSON.stringify(baseline) !== JSON.stringify(draft)
  }, [server, draft])

  const updateRow = (index: number, patch: Partial<DraftAssertion>) => {
    setDraft((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    )
  }
  const removeRow = (index: number) => {
    setDraft((rows) => rows.filter((_, i) => i !== index))
  }
  const addRow = () => {
    setDraft((rows) => [
      ...rows,
      { name: '', criteria: '', severity: 'required' },
    ])
  }

  const handleSave = () => {
    saveMutation.mutate(
      { draft, server },
      {
        onSuccess: () => {
          toast.success('Assertions saved')
          setIsExpanded(false)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to save assertions')
        },
      }
    )
  }

  const handleCancel = () => {
    setDraft(server.map(toDraft))
  }

  return (
    <DutyCard>
      <DutyCardHeader
        icon={<ListChecks />}
        title="Assertions"
        expanded={isExpanded}
        onToggleExpanded={() => setIsExpanded((prev) => !prev)}
        expandLabel="Edit assertions"
        collapseLabel="Collapse assertions"
        action={
          server.length > 0 ? (
            <span className="text-[12px] font-semibold text-neutral-low-contrast">
              {server.length}
            </span>
          ) : null
        }
      />
      <DutyCardBody>
        <DutyCardContent>
          {assertionsQuery.isLoading || assertionsQuery.isPending ? (
            <p className="m-0 text-[13px] text-neutral-low-contrast">
              Loading assertions...
            </p>
          ) : assertionsQuery.isError ? (
            <p className="text-xs text-destructive">
              Unable to load assertions.
            </p>
          ) : isExpanded ? (
            <div className="flex flex-col gap-3">
              {draft.map((row, index) => (
                <AssertionEditRow
                  key={row.id ?? `new-${index}`}
                  row={row}
                  index={index}
                  onChange={updateRow}
                  onRemove={removeRow}
                />
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full border-dashed text-primary-solid"
                onClick={addRow}>
                <Plus className="size-4" />
                Add assertion
              </Button>
            </div>
          ) : server.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {server.map((assertion) => (
                <article
                  key={assertion.id}
                  className="grid gap-2 py-3 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="m-0 text-[14.5px] font-bold text-primary-high-contrast">
                      {assertion.name}
                    </h3>
                    <Badge
                      variant="outline"
                      className={cn(
                        'px-[7px] py-[2px] text-[11px] font-semibold',
                        severityClassName(assertion.severity)
                      )}>
                      {formatSeverity(assertion.severity)}
                    </Badge>
                  </div>
                  <p className="m-0 text-[13px] leading-[1.55] text-neutral-low-contrast">
                    {assertion.criteria}
                  </p>
                </article>
              ))}
            </div>
          ) : (
            <p className="m-0 text-[13px] text-neutral-low-contrast">
              No assertions configured.
            </p>
          )}
        </DutyCardContent>
        {isExpanded ? (
          <DutyCardEditFooter
            onCancel={handleCancel}
            onSave={handleSave}
            onCollapse={() => setIsExpanded(false)}
            isDirty={isDirty}
            isSaving={saveMutation.isPending}
          />
        ) : null}
      </DutyCardBody>
    </DutyCard>
  )
}
